import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { PageHeader } from '@/shared/ui/layout/PageHeader';
import { IconButton } from '@/shared/ui/buttons/IconButton';
import { Spinner } from '@/shared/ui/feedback/Spinner';
import { ErrorState } from '@/shared/ui/feedback/ErrorState';
import { PaperclipIcon, SendIcon, TriangleAlertIcon, XIcon } from '@/shared/ui/icons';
import { useToast } from '@/shared/hooks/useToast';
import { useMutate } from '@/shared/hooks/useMutate';
import { useChatSocket } from '@/shared/hooks/useChatSocket';
import { apiChatMessagePost, apiChatMessagesGet, useChat } from '@/entities/support';
import type { ApiSupportMessage } from '@/shared/types/api/support';
import { WidgetsSupportChatMessageBubble } from './MessageBubble';

// Fallback-only: while the realtime socket is DISCONNECTED we do a slow
// safety refetch. There is no per-second polling — the socket delivers
// messages in realtime, and we sync once on every (re)connect.
const FALLBACK_REFETCH_MS = 20_000;
const MAX_FILE_BYTES = 5 * 1024 * 1024;

const mergeMessages = (
  current: ApiSupportMessage[],
  incoming: ApiSupportMessage[],
): ApiSupportMessage[] => {
  if (incoming.length === 0) return current;
  const seen = new Set(current.map((m) => m.id));
  const additions = incoming.filter((m) => !seen.has(m.id));
  if (additions.length === 0) return current;
  return [...current, ...additions];
};

const isBlockedError = (error: unknown): boolean =>
  isAxiosError(error) &&
  error.response?.status === 403 &&
  (error.response.data as { code?: string } | undefined)?.code === 'blocked';

export const WidgetsSupportChatMain = () => {
  const { t } = useTranslation('support');
  const toast = useToast();
  const chat = useChat();

  const [messages, setMessages] = useState<ApiSupportMessage[]>([]);
  const [text, setText] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [blocked, setBlocked] = useState(false);
  const [socketConnected, setSocketConnected] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  // Always read the current tail id without re-creating callbacks/effects.
  const messagesRef = useRef<ApiSupportMessage[]>([]);
  messagesRef.current = messages;

  // Seed local messages from the initial history load.
  useEffect(() => {
    if (chat.data) {
      setMessages(chat.data.messages);
      setBlocked(chat.data.is_blocked);
    }
  }, [chat.data]);

  // Pull anything created after our last known message and merge it in.
  const sync = useCallback(async () => {
    const afterId = messagesRef.current.at(-1)?.id;
    try {
      const fresh = await apiChatMessagesGet(afterId);
      if (fresh.length > 0) {
        setMessages((prev) => mergeMessages(prev, fresh));
      }
    } catch {
      // Best-effort; the socket / next sync will recover.
    }
  }, []);

  // Realtime delivery over WebSocket — replaces the per-second polling.
  useChatSocket({
    enabled: chat.isSuccess,
    onMessage: (message) => setMessages((prev) => mergeMessages(prev, [message])),
    onOpen: () => void sync(), // one sync per (re)connect to catch missed messages
    onConnectionChange: setSocketConnected,
  });

  // Fallback only: slow safety refetch while the socket is disconnected.
  useQuery({
    queryKey: ['support', 'chat', 'fallback-sync'],
    queryFn: async () => {
      await sync();
      return null;
    },
    enabled: chat.isSuccess && !socketConnected,
    refetchInterval: FALLBACK_REFETCH_MS,
    refetchIntervalInBackground: false,
    staleTime: 0,
    gcTime: 0,
  });

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const send = useMutate({
    api: apiChatMessagePost,
    onSuccess: (message: ApiSupportMessage) => {
      setMessages((prev) => mergeMessages(prev, [message]));
      setText('');
      setFile(null);
    },
    onError: (error, message) => {
      if (isBlockedError(error)) {
        setBlocked(true);
        toast.error(t('blocked'));
        return;
      }
      toast.error(message || t('sendError'));
    },
  });

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const selected = event.target.files?.[0];
    event.target.value = '';
    if (!selected) return;
    if (selected.size > MAX_FILE_BYTES) {
      toast.error(t('fileTooLarge'));
      return;
    }
    setFile(selected);
  };

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault();
    if (send.isPending) return;
    const trimmed = text.trim();
    if (!trimmed && !file) return;
    send.mutate({ text: trimmed || undefined, file: file ?? undefined });
  };

  return (
    <div className="flex h-full flex-col">
      <PageHeader title={t('chatTitle')} showBack backTo="/support" />

      {blocked && (
        <div className="flex items-center gap-2 border-b border-red-200 bg-red-50 px-4 py-2.5 text-sm font-medium text-danger">
          <TriangleAlertIcon className="h-4 w-4 shrink-0" aria-hidden />
          <span>{t('blockedBanner')}</span>
        </div>
      )}

      <div className="flex-1 overflow-y-auto px-4 py-4">
        <div className="mx-auto flex w-full max-w-2xl flex-col gap-3">
          {chat.isLoading ? (
            <div className="flex justify-center py-8">
              <Spinner size="md" />
            </div>
          ) : chat.isError ? (
            <ErrorState message={chat.error.message} onRetry={() => void chat.refetch()} />
          ) : messages.length === 0 ? (
            <p className="py-12 text-center text-sm text-ink-muted">{t('chatEmpty')}</p>
          ) : (
            messages.map((message) => (
              <WidgetsSupportChatMessageBubble key={message.id} message={message} />
            ))
          )}
          <div ref={bottomRef} />
        </div>
      </div>

      <form
        onSubmit={handleSubmit}
        className="border-t border-slate-200 bg-white px-4 py-3"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 0.75rem)' }}
      >
        <div className="mx-auto flex w-full max-w-2xl flex-col gap-2">
          {file && (
            <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
              <PaperclipIcon className="h-4 w-4 shrink-0 text-ink-muted" aria-hidden />
              <span className="min-w-0 flex-1 truncate text-ink">{file.name}</span>
              <IconButton
                label={t('removeAttachment')}
                variant="ghost"
                size="sm"
                onClick={() => setFile(null)}
              >
                <XIcon className="h-4 w-4" />
              </IconButton>
            </div>
          )}
          <div className="flex items-end gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt,.zip"
              className="hidden"
              onChange={handleFileChange}
            />
            <IconButton
              label={t('attach')}
              variant="ghost"
              onClick={() => fileInputRef.current?.click()}
              disabled={blocked}
            >
              <PaperclipIcon className="h-5 w-5" />
            </IconButton>
            <input
              type="text"
              value={text}
              onChange={(event) => setText(event.target.value)}
              placeholder={t('placeholder')}
              disabled={blocked}
              className="min-w-0 flex-1 rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-base text-ink shadow-sm outline-none transition-colors placeholder:text-ink-faint focus:border-brand-500 focus:ring-2 focus:ring-brand-200 disabled:cursor-not-allowed disabled:opacity-60"
            />
            <IconButton
              label={t('send')}
              variant="solid"
              type="submit"
              disabled={blocked || send.isPending || (!text.trim() && !file)}
            >
              {send.isPending ? (
                <Spinner size="sm" className="text-white" />
              ) : (
                <SendIcon className="h-5 w-5" />
              )}
            </IconButton>
          </div>
        </div>
      </form>
    </div>
  );
};
